/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Friday, June 17th 2022, 7:21:16 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import Typography from "@mui/material/Typography";
import FormControl from "@mui/material/FormControl";
import { useForm } from "react-hook-form";
import Stack from "@mui/material/Stack";
import { OSForm } from "ui-widgets/forms";
import { OSAlert, InputHelpDialogWrapper, HelperText } from "ui-widgets";
import { ProcessDefinition, ProcessDefinitionValidationSchema } from "lib/schemas";
import { ValidationResolver } from "lib/validation";
import { useServiceBuilderContext } from "service-builder/providers";
import { COLORSCHEME } from "theme/styles";
import { TranslatableTextFormInput } from "translation/components";
import { ExtraConfigurationsSettingsField } from "./ExtraConfigurationsSettingsField";
import { ProcessFormLinkageSettings } from "./ProcessFormLinkage";

type IAdvancedSettings = Pick<
  ProcessDefinition,
  "service_unit" | "multiple_submission_forms_cta" | "extra_configurations"
>;
const AdvancedSettingsValidationSchema = ProcessDefinitionValidationSchema.pick([
  "service_unit",
  "extra_configurations",
  "multiple_submission_forms_cta",
]);

export const AdvancedSettingsForm = () => {
  const {
    useAdvancedSettingsFormReturn,
    currentProcessDefinition,
    isUpdatingSettings,
    isSuccesfulUpdate,
    isUpdateError,
    handleValidForm,
  } = useAdvancedSettingsFormLogic();

  return (
    <OSForm
      onFormSubmit={useAdvancedSettingsFormReturn.handleSubmit(handleValidForm)}
      validationSchema={AdvancedSettingsValidationSchema}
      useFormReturn={useAdvancedSettingsFormReturn}
      isSubmitting={isUpdatingSettings}
    >
      <Stack spacing={2}>
        {isSuccesfulUpdate ? <SuccessfulUpdate processDefinition={currentProcessDefinition} /> : null}

        {isUpdateError ? <FailedUpdate processDefinition={currentProcessDefinition} /> : null}

        <Typography variant="h5" color={COLORSCHEME.primary}>
          Submission Form Initiation Restrictions
        </Typography>
        <HelperText>Restrict process initiation via form submissions to a set of users for each form.</HelperText>
        <FormControl>
          <ProcessFormLinkageSettings />
        </FormControl>
        <Typography variant="h5" color={COLORSCHEME.primary}>
          General
        </Typography>
        <HelperText>Provide more general settings.</HelperText>
        <InputHelpDialogWrapper
          helpTitle="Service Unit"
          helpContent="The name representing a unit of your service. This will be used throughout the system to idenitify units of your service and automatically pluralize them."
        >
          <TranslatableTextFormInput<ProcessDefinition> schema_field_key="service_unit" label="Service Unit" />
        </InputHelpDialogWrapper>
        <InputHelpDialogWrapper
          helpTitle="Multiple Form Call to Action"
          helpContent="Call to Action when a user should choose among multiple forms"
        >
          <TranslatableTextFormInput<ProcessDefinition>
            schema_field_key="multiple_submission_forms_cta"
            label="Multiple Form Call to Action"
          />
        </InputHelpDialogWrapper>
        <ExtraConfigurationsSettingsField extra_configurations={currentProcessDefinition.extra_configurations} />
      </Stack>
    </OSForm>
  );
};

const useAdvancedSettingsFormLogic = () => {
  const {
    patchCurrentProcessDefinition,
    processDefinitionContext: {
      currentProcessDefinition,
      processDefinitionPatchState: {
        isPatchingProcessDefinition,
        isSuccessfulProcessDefinitionPatch,
        isProcessDefinitionPatchError,
      },
    },
  } = useServiceBuilderContext();

  const { service_unit, extra_configurations, multiple_submission_forms_cta } = currentProcessDefinition;

  const useAdvancedSettingsFormReturn = useForm<IAdvancedSettings>({
    defaultValues: { service_unit, extra_configurations, multiple_submission_forms_cta },
    mode: "onTouched",
    resolver: ValidationResolver(AdvancedSettingsValidationSchema),
  });

  const handleValidForm = (data: IAdvancedSettings) => {
    patchCurrentProcessDefinition(data);
  };

  const {
    formState: { isSubmitSuccessful, isValidating, isSubmitting, isValid },
  } = useAdvancedSettingsFormReturn;

  const isSuccesfulUpdate = isSubmitSuccessful && isSuccessfulProcessDefinitionPatch;

  const isUpdateError = isSubmitSuccessful && isProcessDefinitionPatchError;

  const isUpdatingSettings = isValidating || isSubmitting || (isValid && isPatchingProcessDefinition);

  return {
    useAdvancedSettingsFormReturn,
    currentProcessDefinition,
    isUpdatingSettings,
    isSuccesfulUpdate,
    isUpdateError,
    handleValidForm,
  };
};

const SuccessfulUpdate = ({ processDefinition }) => (
  <OSAlert severity="success" variant="outlined">
    <p>Successfully updated settings for {processDefinition.title}.</p>
  </OSAlert>
);

const FailedUpdate = ({ processDefinition }) => (
  <OSAlert severity="error" variant="outlined">
    <p>Could not update settings for {processDefinition.title}.</p>
  </OSAlert>
);
